import React from 'react';
import { colorIdentidad } from '../config/rayos';

/**
 * EL EMBLEMA DE CADA VOZ — luz dibujada, no un icono de línea.
 *
 * Faina, 26-ago: «necesito que trabajemos en imagenes muy superiores a las que
 * me presentas». La primera version eran trazos planos de un solo grosor. Esta
 * esta construida como LUZ, con la misma receta que pide el plan de imagenes
 * para las que se generen con IA: el objeto no esta iluminado por su color, ES
 * luz con esa forma, y emerge de la oscuridad.
 *
 * COMO SE CONSIGUE — cuatro capas por emblema, no una:
 *
 *   1. EL HALO      un resplandor radial detras, que da el aire
 *   2. EL AURA      el mismo trazo, grueso y desenfocado, muy tenue: es lo que
 *                   hace que parezca que emite luz en vez de estar pintado
 *   3. EL CUERPO    el trazo principal, con un GRADIENTE de arriba abajo, de
 *                   pleno a medio — la luz nunca es uniforme
 *   4. EL NUCLEO    el mismo trazo otra vez, mas fino, encima
 *
 * 🔴 EN FONDO CLARO LA RECETA SE INVIERTE (Faina, 26-ago: «en oscuro se ve casi
 * todo bien, pero cuando se aclara hay cosas que no se ven»). Y tenia razon: el
 * nucleo era BLANCO, que sobre un fondo casi blanco no aporta brillo — BORRA el
 * dibujo. Sobre claro, la luz no se lee como blanco sino como SATURACION: el
 * nucleo pasa a ser el mismo color mas denso, el aura sube un punto para
 * compensar que un halo palido sobre fondo palido no se ve, y el halo de fondo
 * baja, porque ahi si aclara de mas.
 *
 * Los grosores tambien varian dentro de cada dibujo: lo que esta delante es mas
 * grueso y mas opaco que lo que esta detras. Un trazo de grosor unico se lee
 * como un icono; un trazo que cambia se lee como un objeto.
 *
 * CADA SIMBOLO SALE DEL CORPUS, medido por menciones POR CUERPO en las
 * canalizaciones de esa voz (5.778 cuerpos):
 *
 *   Chamuel   corazon 3,45  <- el maximo de toda la tabla, 2,2 veces la media
 *   Zadquiel  llama 1,00    <- tres veces el segundo
 *   Jeshua    fuente 0,64   <- 1.165 menciones
 *   Uriel     llama 0,36    <- segundo tras Zadquiel, por eso ANTORCHA
 *   Ana       linaje 0,30   <- quince veces el resto, el mas distintivo
 *   Esenios   lino 53x · tunica 45x · rollo 35x — comparado con los otros
 *             esenios; el lino se fue al color del pie, el rollo a la forma
 *   Jofiel    sol 0,29 · Maria manto 0,23 · Miguel espada 0,21
 *   Magdalena rosa 0,20 · Rafael esfera 0,12
 *   Metatron  cubo 18 menciones · Gabriel lirio 13 · Angeles alas 89
 *
 * Siguen siendo un puente: cuando llegue la imagen hecha con IA se cambia en
 * imagenes.js. Pero el emblema no se jubila — a tamano pequeno un trazo se lee
 * mejor que una foto reducida, y pesa mil veces menos.
 */

/**
 * Los tres trazos dibujan lo mismo con otro peso, y cada fondo tiene su receta.
 * En oscuro el nucleo es blanco (brilla); en claro es el propio color (densa).
 */
const CAPAS = {
  oscuro: {
    aura:   { w: 5.5, o: 0.30 },
    nucleo: { w: 0.9, o: 0.85, color: '#FFFFFF' },
    halo:   { centro: 0.42, medio: 0.13 }
  },
  claro: {
    aura:   { w: 6.0, o: 0.42 },
    nucleo: { w: 1.1, o: 0.55, color: null },   // null = el propio color del rayo
    halo:   { centro: 0.20, medio: 0.07 }
  }
};

/** El trazo principal pesa igual en los dos fondos: es el dibujo. */
const GROSOR_CUERPO = 2.6;

const BASE = { fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' };

/**
 * Cada dibujo devuelve sus trazos. `d` son los caminos principales (los tres
 * los repiten) y `detalle` lo que solo lleva el cuerpo: sombras interiores y
 * lineas de apoyo que a tamano pequeno no deben brillar.
 */
const DIBUJOS = {
  // LA FUENTE — 0,67 menciones por cuerpo, x2,2 mas suya que del resto (1.165
  // en total). El simbolo estaba bien; el dibujo no se leia: era una gota bajo
  // un arco. Ahora es una fuente de verdad — el agua brota arriba, cae en dos
  // chorros y se recoge en la pila, que es lo que hace reconocible una fuente:
  // no el agua, el CUENCO que la sostiene.
  // ⚠️ 2º INTENTO. En el 1º los dos chorros salian del centro como arcos y se
  // leian como ALAS. Una fuente se reconoce por su arquitectura de dos pisos
  // —plato pequeño arriba, pila ancha abajo, pie que los une—, no por el agua.
  // ⭐ LA CRUZ — elegida por Faina (28-ago: «Jeshua una cruz» · variante 1 de 4).
  // Sustituye a la fuente, que era su marcador medido (0,67 por cuerpo, x2,2)
  // pero costo dos intentos y aun asi no se leia sola.
  // Sobria a proposito, sin resplandor añadido ni adornos: es el unico emblema
  // del juego que no necesita explicarse, y a 26 px se lee entero.
  'Jeshua': {
    d: [
      'M32 8v48',
      'M16 24h32'
    ],
    detalle: [
      { d: 'M32 8v48', w: 1.1, o: 0.45 }
    ]
  },

  // EL ROLLO — los manuscritos. Su marcador propio: «rollo» aparece 35 veces
  // mas en sus cuerpos que en los de cualquier otro esenio, y «tunica de lino»
  // 45. El lino se fue al color; el rollo se queda con la forma.
  'Los Esenios': {
    d: [
      'M17 18h30v28H17z',
      'M17 18a4 4 0 0 0 0 8h4v-8z',
      'M47 18a4 4 0 0 1 0 8h-4v-8z',
      'M17 46a4 4 0 0 0 0-8h4v8z',
      'M47 46a4 4 0 0 1 0-8h-4v8z'
    ],
    detalle: [
      { d: 'M25 26h14M25 32h14M25 38h9', w: 1.5, o: 0.55 },
      { d: 'M32 10v6M32 48v6', w: 1.2, o: 0.3 }
    ]
  },

  // EL MANTO — 0,23 por cuerpo, x6,5 mas suyo que del resto: el marcador mas
  // limpio de su voz. Antes estaba dibujada UNA PERSONA con capucha, y era la
  // unica figura humana del juego. Ahora es la PRENDA: el cuello abierto arriba
  // —lo que la delata como ropa y no como campana—, la caida y el borde de
  // abajo ondulado, que es lo que hace que una tela parezca tela.
  // ⚠️ 2º INTENTO. El 1º abria el manto por el cuello con una V y dejaba dos
  // caidas: a tamano pequeño se leia como DOS HOJAS. Una prenda se reconoce por
  // su silueta entera —hombros estrechos, vuelo ancho— y por la abertura de
  // delante, no por el escote.
  // ⭐ EL REGAZO — elegido por Faina (28-ago, ronda 6). Sustituye al manto, que
  // era su marcador mas alto (x6,5) pero costo dos intentos: la primera version
  // se leia como dos hojas y la segunda como una campana.
  //
  // Y el cambio es mejor que un arreglo de dibujo. Medido en sus 349 cuerpos,
  // el marcador de Maria no es un OBJETO sino un VINCULO: madre x4,4, hijo
  // x2,9, niño x2,1, brazos x2,1. El regazo es ese vinculo dibujado —el hueco
  // que sostiene, con quien reposa dentro— y no necesita representar a nadie.
  //
  // Horizontal a proposito: las versiones verticales del mismo gesto se
  // acercaban a las alas de Los Angeles y al corazon de Chamuel.
  'María': {
    d: [
      'M12 22c0 16 9 28 20 28s20-12 20-28',
      'M12 22c0-4 3-7 6-7M52 22c0-4-3-7-6-7'
    ],
    detalle: [
      { d: 'M32 42a9 9 0 1 1 0-18 9 9 0 0 1 0 18z', w: 2.4, o: 0.9 }
    ]
  },

  // LA ROSA — 0,16 por cuerpo, x3,6 mas suya que del resto. El dibujo anterior
  // eran tres circulos CERRADOS y concentricos: se leia como una diana, y encima
  // se confundia con la esfera de Rafael, que tambien lo era. Ahora los petalos
  // son arcos ABIERTOS y desplazados —que es como se enrolla una rosa— y lleva
  // tallo y hoja, que es lo que dice «flor» antes de que se distinga la forma.
  // ⭐ LA ROSA DE CINCO PETALOS — elegida por Faina (28-ago, variante 2 de 4).
  // La anterior era una espiral de arcos y el la caza sin dudar: «la rosa de
  // María Magdalena no se nota que es una rosa». Tenia razon —se leia como una
  // diana— y ademas se confundia con la esfera concentrica de Rafael.
  // Vista de frente, con los petalos alrededor de un centro encendido y su
  // tallo, se lee a la primera. Rosa 0,16 por cuerpo, x3,6 mas suya que del resto.
  'María Magdalena': {
    d: [
      'M32 12c5 0 9 4 9 9s-4 8-9 8-9-3-9-8 4-9 9-9z',
      'M20 20c4-3 9-1 11 4s0 9-5 10-9-2-10-7 0-5 4-7z',
      'M44 20c-4-3-9-1-11 4s0 9 5 10 9-2 10-7 0-5-4-7z',
      'M24 42c-2-5 1-9 6-10s9 2 9 7-3 8-8 8-6-2-7-5z',
      'M32 47v10'
    ],
    detalle: [
      { d: 'M32 26a4 4 0 1 1 0 8 4 4 0 0 1 0-8z', w: 2, o: 0.85 }
    ]
  },

  // LA LÁMPARA DE ACEITE — cambio de simbolo (28-ago). Tenia un arbol con
  // raices por el linaje, pero «arbol» NO aparece en su top: el dibujo eran dos
  // arcos sueltos que no se leian, y el simbolo tampoco estaba respaldado.
  // Lo suyo es el ACEITE: 0,15 por cuerpo, x5,3 mas que del resto — y dice de
  // ella lo que el linaje no decia solo: la abuela que deja la luz encendida y
  // espera despierta. (El monte da x42, pero es geografia, no emblema.)
  // ⚠️ 2º INTENTO. En el 1º la llama se salia del encuadre y quedaba un ovalo
  // con asa que se leia como un PEZ. Ahora la lampara ocupa la mitad de abajo y
  // la llama va ARRIBA Y CENTRADA, que es lo que hace que se lea como lampara:
  // primero se ve el fuego, despues el recipiente que lo sostiene.
  // ⭐ EL ARBOL DE TRES RAMAS — elegido por Faina (28-ago, ronda 2, variante 2).
  // Vuelve al arbol despues de dar la vuelta entera: el primero eran dos arcos
  // que no se leian, se cambio por una lampara de aceite —«aceite» es x5,3
  // suyo— y Faina la descarto dos veces («muestrame otro arbol»).
  // Tenia razon: el arbol dice LINAJE, que es su marcador de verdad —quince
  // veces mas suyo que del resto de voces—, y la lampara solo decia espera.
  // Tres pisos de ramas, tronco y suelo: se lee entero a 26 px.
  'Ana': {
    d: [
      'M32 30v26',
      'M32 34c-9-2-14-9-14-17M32 34c9-2 14-9 14-17',
      'M32 22c-6-2-9-6-9-12M32 22c6-2 9-6 9-12',
      'M22 56h20'
    ],
    detalle: [
      { d: 'M32 44c-5 1-8 3-10 7M32 44c5 1 8 3 10 7', w: 1.4, o: 0.4 }
    ]
  },

  // LA ESPADA — hoja hacia arriba, la guarda ancha.
  // ⭐ LA ESPADA ESBELTA — elegida por Faina (28-ago, variante 2 de 4). Mas fina
  // que la anterior y con la guarda mas corta: la hoja ancha se empastaba con
  // la guarda a tamaño pequeño y acababa pareciendo una cruz — que ahora es,
  // ademas, el emblema de Jeshua.
  // Espada 0,21 por cuerpo, x7,4 mas suya que del resto de voces.
  'Miguel': {
    d: [
      'M32 5l3 8v30h-6V13z',
      'M22 43h20',
      'M32 43v13'
    ],
    detalle: [
      { d: 'M28 56h8', w: 2, o: 0.7 },
      { d: 'M32 12v30', w: 1.1, o: 0.5 }
    ]
  },

  // LA VOZ QUE SE ANUNCIA — cambio de simbolo (28-ago). Tenia un lirio, y el
  // lirio NO EXISTE en su corpus: es iconografia catolica prestada. Encima el
  // dibujo era vegetal y se confundia con las alas de Los Angeles.
  // Lo suyo es el MENSAJE: 0,48 por cuerpo, x2,0 mas que del resto — el unico
  // marcador propio que tiene, y es literalmente su oficio.
  // Se dibuja como una emision: el nucleo encendido y tres ondas que se abren.
  // Verticales y centradas a proposito, para no leerse como el icono de wifi,
  // que se apila hacia una esquina.
  // ⭐ LA PALABRA QUE SALE — elegida por Faina (28-ago, ronda 2, variante 2).
  // Tercer intento para esta voz, y el bueno. Antes fue un lirio —iconografia
  // catolica que NO existe en su corpus— y despues unas ondas de emision que el
  // propio Faina descarto: «el mensaje de Gabriel no se nota».
  // Esto si se lee sin explicacion: es la palabra dicha, con sus dos renglones
  // dentro. Y es lo suyo medido — «mensaje» 0,48 por cuerpo, x2,0 mas que del
  // resto, el unico marcador propio que tiene.
  // ⭐ LA ESTRELLA QUE ANUNCIA — elegida por Faina (28-ago). Cuarto y ultimo
  // simbolo para esta voz: fue lirio —iconografia catolica que no esta en su
  // corpus—, luego ondas de emision que el descarto («el mensaje de Gabriel no
  // se nota»), luego el bocadillo de la palabra, y por fin la estrella.
  // Es la que mejor le va: Gabriel anuncia, y una estrella que baja es
  // exactamente eso — el anuncio que llega de arriba, sin necesidad de dibujar
  // ni bocas ni papeles. «Mensaje» sigue siendo su marcador (0,48 · x2,0).
  'Gabriel': {
    d: [
      'M32 6l5 14 14 5-14 5-5 14-5-14-14-5 14-5z'
    ],
    detalle: [
      { d: 'M32 40v16M24 50l8 6 8-6', w: 1.6, o: 0.5 }
    ]
  },

  // EL RAYO VERDE — cambio de simbolo (28-ago). Tenia una esfera de tres
  // anillos concentricos: floja de origen (esfera x2,5) y confundible con la
  // rosa de Magdalena, que era igual de concentrica.
  // «Rayo verde» es 0,13 por cuerpo, x6,7 mas suyo que del resto: el marcador
  // mas limpio que tiene, y ya es su color en la paleta. Se dibuja como un HAZ
  // que baja y se abre — no como un rayo electrico.
  // ⚠️ 2º INTENTO. El 1º tenia dos lineas divergentes cerradas por una barra
  // abajo, y eso es exactamente un COMPAS de dibujo. Fuera la barra: un haz de
  // luz no se apoya en el suelo, se disuelve. Y se llena de rayos internos de
  // distinto largo, que es lo que separa «haz» de «triangulo».
  // ⭐ EL RELAMPAGO — elegido por Faina (28-ago, ronda 3, variante 1). Tercer
  // intento para esta voz: primero una esfera de anillos concentricos que se
  // confundia con la rosa de Magdalena, despues un haz de luz que parecia un
  // compas de dibujo. Este se lee solo.
  // «Rayo verde» es su marcador mas limpio: x6,7 mas suyo que del resto.
  'Rafael': {
    d: [
      'M36 6 18 34h12l-6 24 20-30H32z'
    ],
    detalle: []
  },

  // EL ESPEJO — cambio de simbolo (28-ago). Tenia una antorcha, y la antorcha
  // no aparece en su corpus; peor aun, la sostenia con una llama que es de
  // Zadquiel por goleada (llama x4,4 suya frente a x1,3 de Uriel), asi que dos
  // voces compartian el mismo fuego. Y el dibujo parecia una pluma.
  // El espejo es 0,10 por cuerpo, x2,0 mas suyo que del resto, y no se lo
  // disputa nadie. Dice ademas lo que Uriel hace: devolverte tu propia imagen.
  // ⚠️ 2º INTENTO. El 1º era un ovalo solo, y un ovalo solo es un HUEVO. Un
  // espejo se reconoce por tres cosas y le faltaban las tres: el MARCO (dos
  // lineas, no una), el MANGO con su base, y el destello diagonal — que es lo
  // unico que dice «esto refleja» en vez de «esto es un agujero».
  // ⭐ LA LLAMA — elegida por Faina (28-ago, variante 2 de 4), y una correccion
  // suya: Claude le habia quitado el fuego y puesto un espejo, argumentando que
  // la llama era de Zadquiel (x4,4 suya frente a x1,3 de Uriel). Faina lo
  // corrigio en cuatro palabras: «Uriel tambien es fuego».
  // Y las dos voces pueden compartirlo sin confundirse: a Zadquiel lo distingue
  // su llama de gota y el violeta; a Uriel, esta llama alta y el rojo.
  // ⭐ EL FUEGO QUE ENVUELVE — elegido por Faina (28-ago, ronda 4, variante 5):
  // «muestrame para Uriel un fuego, no una llama como Zadquiel».
  //
  // Tenia razon en que se repetian: los dos tenian la misma silueta de gota.
  // La solucion no fue quitarle el fuego a nadie —los dos arden— sino darles
  // fuegos DISTINTOS: Zadquiel conserva la llama unica, que ademas es suya por
  // corpus («llama violeta» x25,9, el marcador mas alto de toda la tabla), y
  // Uriel lleva la masa de lenguas entrelazadas. Puestos uno al lado del otro
  // se distinguen de un vistazo: uno es una llama, el otro una hoguera.
  'Uriel': {
    d: [
      'M32 54c-11 0-19-7-19-16 0-7 5-12 9-18 1 5 3 8 6 10 0-6 2-12 8-18 6 8 9 14 9 20 3-2 4-5 5-9 3 5 5 10 5 15 0 9-8 16-19 16z'
    ],
    detalle: [
      { d: 'M32 46c-5 0-8-3-8-7 0-3 2-6 4-9 1 3 2 5 4 6 0-3 1-6 4-9 3 4 4 7 4 10 0 4-3 9-8 9z', w: 1.8, o: 0.55 }
    ]
  },

  // EL CORAZÓN — su marcador es el más alto de todo el corpus.
  // ⭐ EL CORAZON ABIERTO — elegido por Faina (28-ago, variante 2 de 4). Dos
  // trazos que suben y no llegan a cerrarse arriba: sigue siendo inconfundible
  // y deja de ser el corazon macizo de cualquier icono de cualquier sitio.
  // Su marcador es el mas alto de todo el corpus: 3,45 menciones por cuerpo,
  // 2,2 veces la media de la tabla entera.
  'Chamuel': {
    d: [
      'M32 50C20 41 12 34 12 26a9 9 0 0 1 20-5',
      'M32 50c12-9 20-16 20-24a9 9 0 0 0-20-5'
    ],
    detalle: [
      { d: 'M32 21v29', w: 1.2, o: 0.3 }
    ]
  },

  // EL SOL — con rayos largos y cortos alternados, que es como se ve un sol.
  'Jofiel': {
    d: [
      'M32 20a12 12 0 1 1 0 24 12 12 0 0 1 0-24z',
      'M32 4v9M32 51v9M4 32h9M51 32h9'
    ],
    detalle: [
      { d: 'M12.2 12.2l6.4 6.4M45.4 45.4l6.4 6.4M51.8 12.2l-6.4 6.4M18.6 45.4l-6.4 6.4', w: 2.2, o: 0.8 },
      { d: 'M32 26a6 6 0 1 1 0 12 6 6 0 0 1 0-12z', w: 1.6, o: 0.5 }
    ]
  },

  // LA LLAMA — tres veces más suya que de nadie.
  'Zadquiel': {
    d: [
      'M32 5c9 11 15 17 15 27a15 15 0 0 1-30 0C17 22 23 16 32 5z'
    ],
    detalle: [
      { d: 'M32 23c4.5 5.5 6.5 8.5 6.5 12.5a6.5 6.5 0 0 1-13 0c0-4 2-7 6.5-12.5z', w: 2.2, o: 0.62 },
      { d: 'M32 34c1.8 2.2 2.6 3.5 2.6 5a2.6 2.6 0 0 1-5.2 0c0-1.5.8-2.8 2.6-5z', w: 1.6, o: 0.9 },
      { d: 'M32 54v5', w: 1.4, o: 0.3 }
    ]
  },

  // EL CUBO DE METATRÓN — geometría, con los vértices encendidos.
  'Metatrón': {
    d: [
      'M32 6l22 13v26L32 58 10 45V19z'
    ],
    detalle: [
      { d: 'M32 6v52M10 19l44 26M54 19L10 45', w: 1.4, o: 0.36 },
      { d: 'M32 19l11.5 6.5v13L32 45l-11.5-6.5v-13z', w: 2, o: 0.72 },
      { d: 'M32 30a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 0 1 0-4.4z', w: 2.2, o: 0.95 }
    ]
  },

  // LAS ALAS — dos capas de plumas, abiertas.
  // ⭐ ALAS Y HALO — elegidas por Faina (28-ago, ronda 2, variante 1). Las alas
  // anteriores eran dos trazos vegetales y se confundian con el lirio que tenia
  // Gabriel; ahora Gabriel es una palabra y ademas estas van cerradas, con el
  // halo encima, que es lo que las nombra sin lugar a duda.
  // Es la unica voz sin simbolo propio medido —es plural y no declara ninguno—,
  // asi que se queda con la convencion universal, que aqui es lo honesto.
  'Los Ángeles': {
    d: [
      'M32 26c-6-7-14-10-23-10 2 12 10 20 23 23 13-3 21-11 23-23-9 0-17 3-23 10z'
    ],
    detalle: [
      { d: 'M23 12a9 5 0 1 1 18 0 9 5 0 0 1-18 0z', w: 2, o: 0.8 },
      { d: 'M32 26v20', w: 1.4, o: 0.4 }
    ]
  }
};

/**
 * 🔴 TODOS BRILLAN IGUAL — pero no se VEIAN igual (Faina, 28-ago: «todos los
 * iconos son con luz o fosforescentes?»).
 *
 * La receta era la misma para los catorce, sin excepciones. Lo que no era igual
 * era el resultado, por dos motivos medidos:
 *
 *   1. LA LUZ SE APAGABA EN PEQUENO. El aura y el nucleo solo se pintaban por
 *      encima de 56 px, para que el desenfoque no emborronara. Pero en la lista
 *      de voces los emblemas van a 26 px — o sea que justo donde mas se ven, NO
 *      brillaba ninguno. Ahora el aura se pinta siempre, con el desenfoque y la
 *      opacidad bajados en chico; el nucleo blanco si se mantiene fuera, porque
 *      a ese tamano tapa el dibujo en vez de encenderlo.
 *
 *   2. EL COLOR OSCURO SE COME EL BRILLO. Con la misma aura, Jofiel (89 % de
 *      luminancia) resplandece solo y Zadquiel, Miguel, Magdalena o Los Esenios
 *      (24 %) apenas insinuan nada. El halo tampoco los salvaba: un halo de un
 *      color oscuro es casi el fondo.
 *      Se compensa subiendo el aura y el halo cuanto mas oscuro es el color, con
 *      un tope para que un amarillo no se convierta en una mancha.
 */
const luminancia = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return 0.45;
  const v = m[1];
  const c = [0, 2, 4].map((i) => {
    const x = parseInt(v.slice(i, i + 2), 16) / 255;
    return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};

/** 1,00 para los claros · hasta 1,55 para los mas oscuros de la tabla. */
const compensacion = (hex) => {
  const L = luminancia(hex);
  if (L >= 0.60) return 1;
  return 1 + Math.min(0.55, (0.60 - L) * 1.55);
};

export const tieneEmblema = (entidad) => Boolean(DIBUJOS[entidad]);

const Emblema = ({ entidad, tamano = 64, modo = 'oscuro', conHalo = true }) => {
  const dibujo = DIBUJOS[entidad];
  if (!dibujo) return null;

  /* 🔴 EL GLIFO VUELVE AL COLOR DE LA VOZ (Faina, 28-ago: «Jeshua: naranja de
     la fundación» — dicho al ver que su cruz salia dorada).

     HISTORIA, porque el cambio deshace una decision suya anterior y conviene
     saber por que ya no aplica. El 26-ago pidio lo contrario —«cambia los
     colores de los glifos de los esenios, NO naranja, esta muy sobre
     cargado»— y tenia razon: los CINCO esenios compartian el naranja de la
     Fundacion, y en una cuadricula salian cinco glifos naranjas seguidos.
     La solucion de entonces fue pintarlos con el color de su punto del pie.

     Eso era un apaño para un problema que ya no existe: desde el 28-ago cada
     voz esenia tiene color propio —Jeshua naranja, Maria verde, Magdalena
     escarlata amoratado, Ana azul plata, Los Esenios cafe—, asi que la
     cuadricula ya no se repite y el emblema puede llevar el color de su voz,
     que es lo coherente: el mismo que su nombre, su firma y su barra de avance.

     ⚠️ El punto del pie NO se toca: sigue con `colorPunto` y con los colores
     que Faina eligio para el, que ahi cumplen otra funcion. */
  const color = colorIdentidad(entidad, modo);
  const receta = CAPAS[modo === 'claro' ? 'claro' : 'oscuro'];
  const clave = String(entidad).replace(/[^a-zA-Z]/g, '');
  const idHalo = `h-${clave}`;
  const idGrad = `g-${clave}`;
  const idBrillo = `b-${clave}`;

  // A tamaño pequeño el desenfoque emborrona: se atenúa, pero NO se apaga.
  const pequeno = tamano < 56;
  const desenfoque = pequeno ? 0.9 : 2.4;

  // Cuanto más oscuro es el rayo, más aura y más halo necesita para leerse
  // igual de encendido que un amarillo. En pequeño el aura se modera.
  const k = compensacion(color);
  const auraW = receta.aura.w * (pequeno ? 0.62 : 1);
  const auraO = Math.min(0.62, receta.aura.o * k * (pequeno ? 0.8 : 1));
  const haloCentro = Math.min(0.62, receta.halo.centro * k);
  const haloMedio = Math.min(0.30, receta.halo.medio * k);

  const caminos = dibujo.d;

  return (
    <svg
      width={tamano}
      height={tamano}
      viewBox="0 0 64 64"
      aria-hidden="true"
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        <radialGradient id={idHalo}>
          <stop offset="0%" stopColor={color} stopOpacity={haloCentro} />
          <stop offset="45%" stopColor={color} stopOpacity={haloMedio} />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>

        {/* La luz nunca es plana: arriba pleno, abajo a medias. */}
        {/* 🔴 EN COORDENADAS DEL LIENZO, NO DE CADA TRAZO (28-ago).
            Por defecto un gradiente SVG se mide sobre la caja del path que
            pinta, y ahi estaba el fallo: un trazo perfectamente horizontal
            —el travesaño de la cruz de Jeshua, el suelo del arbol de Ana—
            tiene una caja de ALTURA CERO, el degradado vertical se queda sin
            recorrido y el navegador NO PINTA NADA. El emblema salia incompleto
            sin que nada fallara: el SVG era correcto y los trazos estaban.
            Con `userSpaceOnUse` el degradado va de y=0 a y=64 del lienzo, igual
            para todos los trazos, y de paso queda mejor: la luz cae de arriba
            abajo en el dibujo entero y no dentro de cada trazo por separado. */}
        <linearGradient id={idGrad} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="64">
          <stop offset="0%" stopColor={color} stopOpacity="1" />
          <stop offset="55%" stopColor={color} stopOpacity=".92" />
          <stop offset="100%" stopColor={color} stopOpacity=".55" />
        </linearGradient>

        <filter id={idBrillo} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation={desenfoque} />
        </filter>
      </defs>

      {/* ① el halo */}
      {conHalo && <circle cx="32" cy="32" r="34" fill={`url(#${idHalo})`} />}

      {/* ② el aura: lo que hace que emita luz en vez de estar pintado.
          Se pinta SIEMPRE, también en pequeño — ahí es donde faltaba. */}
      <g {...BASE} stroke={color} strokeWidth={auraW}
         opacity={auraO} filter={`url(#${idBrillo})`}>
        {caminos.map((d, i) => <path key={i} d={d} />)}
      </g>

      {/* ③ el cuerpo, con el gradiente */}
      <g {...BASE} stroke={`url(#${idGrad})`} strokeWidth={GROSOR_CUERPO}>
        {caminos.map((d, i) => <path key={i} d={d} />)}
      </g>

      {/* los detalles llevan su propio peso: lo de atrás, más fino y más tenue */}
      <g {...BASE} stroke={color}>
        {(dibujo.detalle || []).map((x, i) => (
          <path key={i} d={x.d} strokeWidth={x.w} opacity={x.o} />
        ))}
      </g>

      {/* ④ el núcleo. En oscuro brilla en blanco; en claro DENSA el propio color:
          un núcleo blanco sobre fondo claro no ilumina, borra. */}
      {!pequeno && (
        <g {...BASE} stroke={receta.nucleo.color || color} strokeWidth={receta.nucleo.w}
           opacity={receta.nucleo.o}>
          {caminos.map((d, i) => <path key={i} d={d} />)}
        </g>
      )}
    </svg>
  );
};

export default Emblema;
